import React, { useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import { v4 as uuidv4 } from 'uuid';

/*
 Props accepted: promotion | record | data | row | item | (GenericListPage wrappers)
 onClose, onSaveSuccess
*/
const PromotionForm = (props) => {
  const api = useApi();
  const {
    onClose = () => {},
    onSaveSuccess = () => {}
  } = props;

  // robust source detection (handles record.data / record.record / wrapped prop)
  const getSource = () => {
    let s = props.promotion || props.record || props.data || props.row || props.item || null;
    if (s && typeof s === 'object') {
      if (s.data && typeof s.data === 'object') s = s.data;
      else if (s.record && typeof s.record === 'object') s = s.record;
      else if (s._doc && typeof s._doc === 'object') s = s._doc;
    }
    if (!s) {
      for (const v of Object.values(props)) {
        if (!v || typeof v !== 'object') continue;
        const candidate = v.data || v.record || v._doc || v;
        if (candidate && (candidate.name || candidate.type || candidate.conditions)) {
          s = candidate;
          break;
        }
      }
    }
    return s || null;
  };

  const source = getSource();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const empty = {
    name: '',
    type: 'percentage', // percentage or fixed
    value: '',
    conditions: {
      minPurchaseAmount: '',
      applicableProducts: [],
      applicableCategories: []
    },
    startDate: '',
    endDate: '',
    isActive: true
  };

  const [form, setForm] = useState(empty);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes] = await Promise.all([
          api.get('products', { queryParams: { limit: 1000 } }),
          api.get('categories', { queryParams: { limit: 1000 } })
        ]);
        setProducts(prodRes?.data || prodRes || []);
        setCategories(catRes?.data || catRes || []);
      } catch (err) {
        console.error('Failed loading promotion dropdowns', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // populate when editing
  useEffect(() => {
    const src = getSource();
    if (!src) {
      setForm(empty);
      return;
    }
    setForm({
      name: src.name || '',
      type: src.type || 'percentage',
      value: src.value != null ? src.value : '',
      conditions: {
        minPurchaseAmount: src.conditions?.minPurchaseAmount ?? src.minPurchaseAmount ?? '',
        applicableProducts: Array.isArray(src.conditions?.applicableProducts) ? src.conditions.applicableProducts.map(p => (typeof p === 'string' ? p : (p._id || p.id || ''))) : (Array.isArray(src.applicableProducts) ? src.applicableProducts : []),
        applicableCategories: Array.isArray(src.conditions?.applicableCategories) ? src.conditions.applicableCategories.map(c => (typeof c === 'string' ? c : (c._id || c.id || ''))) : (Array.isArray(src.applicableCategories) ? src.applicableCategories : [])
      },
      startDate: src.startDate ? new Date(src.startDate).toISOString().slice(0,16) : (src.start ? new Date(src.start).toISOString().slice(0,16) : ''),
      endDate: src.endDate ? new Date(src.endDate).toISOString().slice(0,16) : (src.end ? new Date(src.end).toISOString().slice(0,16) : ''),
      isActive: src.isActive != null ? Boolean(src.isActive) : true
    });
    // re-run when source or lookups change
  }, [props, products.length, categories.length]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('conditions.')) {
      const key = name.split('.')[1];
      if (key === 'applicableProducts' || key === 'applicableCategories') {
        // multiple select handled separately
        return;
      }
      setForm(prev => ({ ...prev, conditions: { ...prev.conditions, [key]: value } }));
      return;
    }
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (e, field) => {
    const opts = Array.from(e.target.selectedOptions).map(o => o.value);
    setForm(prev => ({ ...prev, conditions: { ...prev.conditions, [field]: opts } }));
  };

  const validate = () => {
    setError(null);
    if (!form.name) return 'Name is required';
    if (!form.type) return 'Type is required';
    if (form.value === '' || isNaN(Number(form.value))) return 'Value is required and must be a number';
    return null;
  };

  const handleSave = async (e) => {
    e && e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      name: form.name,
      type: form.type,
      value: form.value !== '' ? Number(form.value) : undefined,
      conditions: {
        minPurchaseAmount: form.conditions.minPurchaseAmount !== '' ? Number(form.conditions.minPurchaseAmount) : undefined,
        applicableProducts: form.conditions.applicableProducts && form.conditions.applicableProducts.length > 0 ? form.conditions.applicableProducts : undefined,
        applicableCategories: form.conditions.applicableCategories && form.conditions.applicableCategories.length > 0 ? form.conditions.applicableCategories : undefined
      },
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      isActive: Boolean(form.isActive)
    };

    try {
      const existingId = (getSource() && (getSource()._id || getSource().id)) || null;
      let resp;
      if (existingId) {
        resp = await api.put(`promotions/${existingId}`, payload);
      } else {
        resp = await api.post('promotions', payload);
      }
      const saved = resp?.data ?? resp;
      onSaveSuccess(saved);
      onClose();
    } catch (err) {
      console.error('Save promotion failed', err);
      setError(err?.message || 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="p-4 max-w-3xl">
      {loading && <div className="text-sm text-gray-500 mb-2">Loading...</div>}
      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Name</label>
          <input name="name" value={form.name} onChange={handleChange} className="mt-1 block w-full border rounded p-2" />
        </div>

        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Type</label>
          <select name="type" value={form.type} onChange={handleChange} className="mt-1 block w-full border rounded p-2">
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>

          <label className="text-sm font-medium mt-3">Value</label>
          <input name="value" type="number" step="0.01" value={form.value} onChange={handleChange} className="mt-1 block w-full border rounded p-2" />
        </div>
      </div>

      <div className="p-3 border rounded bg-white mb-4">
        <div className="text-sm font-semibold mb-2">Conditions</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="text-sm">Min Purchase Amount</label>
            <input name="conditions.minPurchaseAmount" type="number" step="0.01" value={form.conditions.minPurchaseAmount} onChange={handleChange} className="mt-1 block w-full border rounded p-2" />
          </div>

          <div>
            <label className="text-sm">Applicable Products</label>
            <select multiple value={form.conditions.applicableProducts} onChange={(e) => handleMultiSelect(e, 'applicableProducts')} className="mt-1 block w-full border rounded p-2 h-32">
              {products.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name || p.sku || (p._id || p.id)}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm">Applicable Categories</label>
            <select multiple value={form.conditions.applicableCategories} onChange={(e) => handleMultiSelect(e, 'applicableCategories')} className="mt-1 block w-full border rounded p-2 h-32">
              {categories.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name || c.title || (c._id || c.id)}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Start Date</label>
          <input name="startDate" type="datetime-local" value={form.startDate} onChange={handleChange} className="mt-1 block w-full border rounded p-2" />
        </div>
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">End Date</label>
          <input name="endDate" type="datetime-local" value={form.endDate} onChange={handleChange} className="mt-1 block w-full border rounded p-2" />
        </div>
        <div className="p-3 border rounded bg-white flex items-center">
          <label className="text-sm font-medium mr-3">Active</label>
          <input name="isActive" type="checkbox" checked={!!form.isActive} onChange={handleChange} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded">{saving ? 'Saving...' : (source ? 'Save Changes' : 'Create Promotion')}</button>
      </div>
    </form>
  );
};

export default PromotionForm;